import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import socket from "../lib/socket";
import { Users, PaperPlaneRight, ShieldStar } from "@phosphor-icons/react";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

function formatTime(iso) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: es });
  } catch {
    return "";
  }
}

function SystemMessage({ msg }) {
  return (
    <div className="flex justify-center my-3">
      <div
        data-testid="community-system-msg"
        className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold"
        style={{
          background: "rgba(212,175,55,0.1)",
          border: "1px solid rgba(212,175,55,0.25)",
          color: "#D4AF37",
        }}
      >
        <ShieldStar className="w-3.5 h-3.5 flex-shrink-0" weight="fill" />
        <span>{msg.text}</span>
      </div>
    </div>
  );
}

function ChatBubble({ msg, isOwn }) {
  const isAdmin = msg.user_role === "admin";
  return (
    <div
      data-testid="community-message"
      className={`flex gap-2.5 mb-3 ${isOwn ? "flex-row-reverse" : ""}`}
    >
      {/* Avatar */}
      <div
        className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-black"
        style={{
          background: isAdmin
            ? "linear-gradient(135deg,#D4AF37,#A07C20)"
            : "rgba(255,255,255,0.1)",
          color: isAdmin ? "#000" : "#fff",
          border: isAdmin ? "none" : "1px solid rgba(255,255,255,0.1)",
        }}
      >
        {(msg.user_name || "?").charAt(0).toUpperCase()}
      </div>

      {/* Content */}
      <div className={`max-w-[70%] ${isOwn ? "items-end" : "items-start"} flex flex-col`}>
        {/* Name row */}
        <div className={`flex items-center gap-2 mb-1 ${isOwn ? "flex-row-reverse" : ""}`}>
          <span className="text-xs font-bold" style={{ color: isAdmin ? "#D4AF37" : "rgba(255,255,255,0.7)" }}>
            {msg.user_name}
          </span>
          {isAdmin && (
            <span
              className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded"
              style={{ background: "rgba(212,175,55,0.15)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.3)" }}
            >
              Admin
            </span>
          )}
          <span className="text-[10px]" style={{ color: "rgba(255,255,255,0.3)" }}>
            {formatTime(msg.created_at)}
          </span>
        </div>

        {/* Bubble */}
        <div
          className="px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed"
          style={isAdmin ? {
            background: "rgba(212,175,55,0.08)",
            border: "1px solid rgba(212,175,55,0.25)",
            borderLeft: "3px solid #D4AF37",
            color: "#fff",
            borderRadius: "4px 16px 16px 16px",
          } : isOwn ? {
            background: "rgba(212,175,55,0.18)",
            color: "#fff",
            borderRadius: "16px 4px 16px 16px",
          } : {
            background: "rgba(255,255,255,0.06)",
            color: "rgba(255,255,255,0.9)",
            borderRadius: "4px 16px 16px 16px",
          }}
        >
          {msg.text}
        </div>
      </div>
    </div>
  );
}

export default function CommunityPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [connected, setConnected] = useState(false);
  const [connError, setConnError] = useState(null);
  const [onlineCount, setOnlineCount] = useState(0);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback((smooth = true) => {
    bottomRef.current?.scrollIntoView({ behavior: smooth ? "smooth" : "instant" });
  }, []);

  useEffect(() => {
    if (!socket.connected) socket.connect();

    const onConnect = () => { setConnected(true); setConnError(null); };
    const onDisconnect = () => setConnected(false);
    const onHistory = (msgs) => {
      setMessages(msgs);
      setTimeout(() => scrollToBottom(false), 50);
    };
    const onMessage = (msg) => {
      setMessages((prev) => [...prev, msg]);
      setTimeout(() => scrollToBottom(true), 50);
    };

    const onConnectError = (err) => {
      setConnected(false);
      const msg = err?.message || "";
      if (msg.includes("unauthorized") || msg.includes("auth")) {
        setConnError("Sesión expirada. Recarga la página para reconectar.");
      } else {
        setConnError("No se pudo conectar al chat. Revisa tu conexión.");
      }
    };
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);
    socket.on("community:history", onHistory);
    socket.on("community:message", onMessage);

    if (socket.connected) setConnected(true);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
      socket.off("community:history", onHistory);
      socket.off("community:message", onMessage);
    };
  }, [scrollToBottom]);

  const retryConnect = () => {
    setConnError(null);
    setConnected(false);
    socket.connect();
  };

  const sendMessage = (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || !connected) return;
    socket.emit("community:send", { text: trimmed });
    setText("");
    inputRef.current?.focus();
  };

  return (
    <div
      className="flex flex-col"
      style={{ height: "calc(100dvh - 140px)", minHeight: 0 }}
    >
      {/* Header */}
      <div
        className="flex-shrink-0 flex items-center justify-between px-4 py-3"
        style={{
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          background: "rgba(10,10,12,0.8)",
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.25)" }}
          >
            <Users className="w-5 h-5" style={{ color: "#D4AF37" }} weight="fill" />
          </div>
          <div>
            <h1 className="font-black text-white text-base leading-tight">Chat Comunidad</h1>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: connected ? "#22C55E" : "#71717A" }}
              />
              <span className="text-[10px] uppercase tracking-widest" style={{ color: connected ? "#22C55E" : connError ? "#ef4444" : "#8E8E99" }}>
                {connected ? "En vivo" : connError ? "Error de conexión" : "Conectando..."}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Banner de error de conexión */}
      {connError && (
        <div className="flex-shrink-0 mx-4 mt-3 p-3 rounded-xl flex items-center justify-between gap-3"
          style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)" }}>
          <span className="text-sm" style={{ color: "#fca5a5" }}>{connError}</span>
          <button onClick={retryConnect}
            className="flex-shrink-0 px-4 py-2 rounded-lg text-sm font-bold"
            style={{ background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)", color: "#ef4444" }}>
            Reintentar
          </button>
        </div>
      )}

      {/* Messages */}
      <div
        className="flex-1 overflow-y-auto px-4 py-4"
        style={{ minHeight: 0 }}
      >
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full gap-3 opacity-40">
            <Users className="w-12 h-12" style={{ color: "#D4AF37" }} />
            <p className="text-sm text-zinc-500">Sé el primero en escribir...</p>
          </div>
        )}
        {messages.map((msg) =>
          msg.is_system ? (
            <SystemMessage key={msg.id} msg={msg} />
          ) : (
            <ChatBubble key={msg.id} msg={msg} isOwn={msg.user_id === user?.id} />
          )
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div
        className="flex-shrink-0 px-4 py-3"
        style={{ borderTop: "1px solid rgba(255,255,255,0.06)", background: "rgba(10,10,12,0.8)" }}
      >
        <form onSubmit={sendMessage} className="flex gap-2">
          <input
            ref={inputRef}
            data-testid="community-input"
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={1000}
            placeholder={connected ? "Escribe un mensaje..." : "Conectando..."}
            disabled={!connected}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm outline-none transition-all"
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#fff",
            }}
            onFocus={(e) => (e.target.style.borderColor = "rgba(212,175,55,0.4)")}
            onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
          />
          <button
            type="submit"
            data-testid="community-send-btn"
            disabled={!text.trim() || !connected}
            className="w-10 h-10 rounded-xl flex items-center justify-center transition-all flex-shrink-0"
            style={{
              background: text.trim() && connected ? "#D4AF37" : "rgba(255,255,255,0.06)",
              color: text.trim() && connected ? "#000" : "#52525B",
            }}
          >
            <PaperPlaneRight className="w-4 h-4" weight="fill" />
          </button>
        </form>
      </div>
    </div>
  );
}
